/* S1 Campaign Hero
   - fade-ins: .s1-ch-fade + IntersectionObserver (same contract as .s1-xc-fade)
   - scroll CTAs: [data-ch-scroll]; "product" falls back to the first <s1-pdp>
   - breakpoint videos: paired desktop/mobile <video data-ch-video> carry
     data-src so only the displayed element ever downloads; a lone desktop
     video serves both breakpoints. Re-checked on breakpoint change. */

const BREAKPOINT = window.matchMedia('(min-width: 768px)');

class S1CampaignHero extends HTMLElement {
  connectedCallback() {
    /* fades */
    if (!this._fadeIo) {
      this._fadeIo = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('is-visible');
            this._fadeIo.unobserve(en.target);
          }
        });
      }, { threshold: 0.2 });
    }
    this.querySelectorAll('.s1-ch-fade:not(.is-visible)').forEach((el) => this._fadeIo.observe(el));

    /* re-connects (theme editor drags) must not stack the click listener;
       the breakpoint listener was removed on disconnect — re-adding the same
       function reference is a spec-level no-op, so this stays idempotent */
    if (this._bound) {
      BREAKPOINT.addEventListener('change', this._loadVideos);
      this._loadVideos();
      this._startSwap();
      return;
    }
    this._bound = true;

    /* scroll CTAs */
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-ch-scroll]');
      if (!btn) return;
      const target = btn.dataset.chScroll;
      const el = document.getElementById(target)
        || (target === 'product' && document.querySelector('s1-pdp'))
        || (target === 'guarantee' && document.querySelector('s1-guarantee'));
      if (!el) return;
      /* the buy box lands on its layout (past the section's top padding);
         the theme's html scroll-padding-top keeps it under the sticky header */
      const anchor = (target === 'product' && el.querySelector('.s1-pdp__layout')) || el;
      anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    /* videos */
    this._loadVideos = () => {
      const desktop = this.querySelector('[data-ch-video="desktop"]');
      const mobile = this.querySelector('[data-ch-video="mobile"]');
      const active = BREAKPOINT.matches ? desktop : (mobile || desktop);
      if (active && !active.src && active.dataset.src) {
        active.src = active.dataset.src;
        active.play().catch(() => {});
      }
    };
    this._loadVideos();
    BREAKPOINT.addEventListener('change', this._loadVideos);

    /* NBL video modal: the play button opens the breakpoint's mp4 full
       screen; the src is set at open and released at close so neither video
       downloads until asked for */
    const modal = this.querySelector('[data-ch-modal]');
    if (modal) {
      const modalVideo = modal.querySelector('[data-ch-modal-video]');
      const closeModal = () => {
        modal.hidden = true;
        modalVideo.pause();
        modalVideo.removeAttribute('src');
        modalVideo.load();
        document.removeEventListener('keydown', this._modalEsc);
      };
      this._modalEsc = (e) => { if (e.key === 'Escape') closeModal(); };
      this.querySelectorAll('[data-ch-modal-open]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const src = BREAKPOINT.matches
            ? (modalVideo.dataset.srcDesktop || modalVideo.dataset.srcMobile)
            : (modalVideo.dataset.srcMobile || modalVideo.dataset.srcDesktop);
          if (!src) return;
          if (modalVideo.getAttribute('src') !== src) modalVideo.src = src;
          modal.hidden = false;
          modalVideo.play().catch(() => {});
          document.addEventListener('keydown', this._modalEsc);
        });
      });
      modal.querySelector('[data-ch-modal-close]').addEventListener('click', closeModal);
    }

    /* NiteKnit word-swap: toggle the A/B pair every 4s (Hero.tsx interval);
       reduced-motion users keep the resting A pair */
    this._startSwap = () => {
      const swap = this.querySelector('[data-ch-swap]');
      if (!swap || this._swapTimer) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this._swapTimer = setInterval(() => swap.classList.toggle('is-b'), 4000);
    };
    this._startSwap();
  }

  disconnectedCallback() {
    if (this._loadVideos) BREAKPOINT.removeEventListener('change', this._loadVideos);
    if (this._fadeIo) this._fadeIo.disconnect();
    if (this._swapTimer) { clearInterval(this._swapTimer); this._swapTimer = null; }
  }
}

if (!customElements.get('s1-campaign-hero')) {
  customElements.define('s1-campaign-hero', S1CampaignHero);
}
